import { repoBranchSubmodulesSchema } from "@arti/shared-source-control/indexing/branchSubmodules";
import {
  SnippetImportance,
  snippetImportanceSchema,
} from "@arti/shared-source-control/indexing/snippetImportance";
import { z } from "zod";

import { coerceToJsonObject } from "../../json";

const stringArray = (desc: string) => z.array(z.string()).describe(desc);

const repoBranchTrackingSchema = z
  .object({
    ahead_by: z.number().int().min(0),
    behind_by: z.number().int().min(0),
    compared_to_branch: z.string().min(1),
  })
  .strict();

export const REPOSITORY_TOOL_KIND_VALUES = [
  "quality",
  "build",
  "test",
  "run",
] as const;
export type RepositoryToolKind = (typeof REPOSITORY_TOOL_KIND_VALUES)[number];

export const REPOSITORY_TOOL_STATUS_VALUES = [
  "error",
  "warning",
  "valid",
  "unvalidated",
] as const;
export type RepositoryToolStatus =
  (typeof REPOSITORY_TOOL_STATUS_VALUES)[number];

export const universalRepositorySchema = z
  .object({
    api: z
      .object({
        authentication: stringArray(
          "Auth mechanisms (e.g., JWT, OAuth2, Shared Secrets)",
        ),
        authorization: stringArray(
          "Access control strategies (e.g., RBAC, Node ID validation)",
        ),
        documentation: stringArray(
          "Links or paths to API specs and reference tables",
        ),
        endpoints: stringArray(
          "Key API routes or patterns (e.g., /internal/*, /webhook/jira)",
        ),
        styles: stringArray("API Paradigms (e.g., REST, JSON-RPC, GraphQL)"),
      })
      .strict(),
    architecture: z
      .object({
        communication: z
          .object({
            protocols: stringArray(
              "Transport protocols (e.g., gRPC, HTTP/1.1, WebSocket)",
            ),
            styles: stringArray(
              "Interface styles (e.g., RESTful JSON, Internal RPC, Webhooks)",
            ),
          })
          .strict(),
        deployment_model: stringArray(
          "How the system is deployed (e.g., Containerized Microservices, Serverless)",
        ),
        patterns: stringArray(
          "Design patterns used (e.g., Command pattern, Sidecar, Event-driven)",
        ),
      })
      .strict(),
    development: z
      .object({
        active_branches: z
          .array(
            z
              .object({
                description: z
                  .string()
                  .describe(
                    "Specific purpose or ticket ID associated with the branch",
                  ),
                is_default: z
                  .boolean()
                  .optional()
                  .describe(
                    "Whether this branch is the repository default branch",
                  ),
                name: z.string().describe("The branch name"),
                submodules: repoBranchSubmodulesSchema.optional(),
                tracking: repoBranchTrackingSchema.optional(),
              })
              .strict(),
          )
          .describe("List of currently active feature or execution branches"),
        default_branch: z.string().describe("The primary branch (e.g., main)"),
        documentation: z
          .object({
            formats: stringArray(
              "Doc formats (e.g., Markdown, D2 architecture diagrams)",
            ),
            locations: stringArray(
              "Where documentation is stored (e.g., /docs, README.md)",
            ),
          })
          .strict(),
        testing: z
          .object({
            frameworks: stringArray("Test runners (e.g., node:test, Vitest)"),
            types: stringArray("Test categories supported"),
          })
          .strict(),
      })
      .strict(),
    identity: z
      .object({
        business_domain: z
          .string()
          .describe(
            "The industry or problem space (e.g., AI Automation, Fintech)",
          ),
        consumers: stringArray(
          "Downstream systems, services, or users relying on this repo",
        ),
        description: z
          .string()
          .nullable()
          .describe("A brief summary of the project purpose"),
        name: z
          .string()
          .describe("The full repository name (e.g., owner/repo)"),
        repository_role: z
          .string()
          .describe(
            "The architectural role (e.g., Orchestrator, Worker, CLI tool)",
          ),
        responsibilities: stringArray(
          "Key functional duties performed by this codebase",
        ),
      })
      .strict(),
    infrastructure: z
      .object({
        cloud: z
          .object({
            platform_services: stringArray(
              "Specific cloud platforms used (e.g., Azure Container Apps, S3)",
            ),
            provider: z
              .string()
              .nullable()
              .describe("Primary cloud vendor (e.g., Azure, AWS, GCP)"),
          })
          .strict(),
        containerization: z
          .object({
            orchestrators: stringArray(
              "Orchestration tools (e.g., Kubernetes, Dapr)",
            ),
            technologies: stringArray(
              "Container tools (e.g., Docker, Docker Compose)",
            ),
          })
          .strict(),
        data: z
          .object({
            caches: stringArray("Caching layers or state stores (e.g., Redis)"),
            databases: stringArray("Primary relational or NoSQL databases"),
            storage: stringArray(
              "Persistent file or blob storage (e.g., Azure Files, EBS)",
            ),
          })
          .strict(),
      })
      .strict(),
    integrations: z
      .object({
        providers: stringArray(
          "External services/APIs (e.g., Anthropic, OpenAI, Jira, Datadog)",
        ),
        webhooks: z
          .object({
            incoming: stringArray("External sources sending data to this repo"),
            outgoing: stringArray("External targets this repo notifies"),
          })
          .strict(),
      })
      .strict(),
    operations: z
      .object({
        ci_cd: z
          .object({
            platform: z
              .string()
              .nullable()
              .describe("CI Provider (e.g., GitHub Actions, Jenkins)"),
            strategies: stringArray(
              "Deployment/Infra strategies (e.g., Bicep IaC, Docker Compose)",
            ),
          })
          .strict(),
        configuration: z
          .object({
            formats: stringArray(
              "Config file formats (e.g., YAML, JSON, .env)",
            ),
            management: stringArray(
              "Config strategy (e.g., Environment variables, Tenant overrides)",
            ),
          })
          .strict(),
        observability: z
          .object({
            logging: stringArray(
              "Logging standards or tools (e.g., Structured logging, Redaction)",
            ),
            monitoring: stringArray(
              "Metrics and health tracking (e.g., Datadog, Teams webhooks)",
            ),
            tracing: stringArray(
              "Correlation and telemetry (e.g., Correlation IDs)",
            ),
          })
          .strict(),
      })
      .strict(),
    stack: z
      .object({
        frameworks: stringArray(
          "Major frameworks (e.g., React, Prisma, Express, Tailwind)",
        ),
        languages: z
          .object({
            primary: stringArray("Main programming languages"),
            supporting: stringArray(
              "Scripting, infrastructure, or config languages (e.g., Bicep, Bash)",
            ),
          })
          .strict(),
        libraries: stringArray("Significant libraries, SDKs, or utilities"),
        runtimes: stringArray(
          "Execution environments and versions (e.g., Node.js 20, Python 3.10)",
        ),
      })
      .strict(),
    tooling: z
      .object({
        build_system: z
          .object({
            commands: stringArray("Standard commands used to compile or build"),
            tools: stringArray("Build engines (e.g., Vite, MSBuild, GCC)"),
          })
          .strict(),
        development_tools: z
          .object({
            cli_utilities: stringArray(
              "Custom internal CLI tools for developers",
            ),
            local_dev_commands: stringArray(
              "Commands to start the project locally",
            ),
            runtime_management: stringArray(
              "Tools for managing runtimes (e.g., asdf, nvm)",
            ),
          })
          .strict(),
        package_management: z
          .object({
            registry: z
              .string()
              .nullable()
              .describe(
                "Registry used (e.g., GitHub Container Registry, npmjs)",
              ),
            tools: stringArray(
              "Tools to manage dependencies (e.g., pnpm, pip, npm)",
            ),
          })
          .strict(),
        testing_commands: z
          .object({
            integration: stringArray("Commands to run integration/E2E tests"),
            linting: stringArray(
              "Commands for static analysis (e.g., eslint, prettier)",
            ),
            unit: stringArray("Commands to run unit tests"),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

export type UniversalRepositoryIdentity = z.infer<
  typeof universalRepositorySchema
>;

export const repoSnippetLineRangeSchema = z
  .object({
    code_snippet: z
      .string()
      .min(1)
      .describe(
        "Verbatim code excerpt for this range when available from deterministic extraction",
      )
      .optional(),
    end_line: z.number().int().min(1),
    start_line: z.number().int().min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.end_line < value.start_line) {
      ctx.addIssue({
        code: "custom",
        message: "end_line must be greater than or equal to start_line",
        path: ["end_line"],
      });
    }
  });

export const repoSnippetReferenceSchema = z
  .object({
    importance: snippetImportanceSchema
      .default(SnippetImportance.Supplementary)
      .describe(
        "Importance rank 1-4: 1 = critical entry point or core contract, 2 = important shared service or key module, 3 = supporting utility or configuration, 4 = supplementary reference",
      ),
    line_ranges: z
      .array(repoSnippetLineRangeSchema)
      .default([])
      .describe(
        "Line ranges for important sections. Include multiple ranges for a file when needed.",
      ),
    path: z
      .string()
      .min(1)
      .describe("Repository-relative file or directory path"),
    path_kind: z
      .enum(["file", "directory"])
      .describe("Whether path points to a file or directory"),
    reusable_elements: z
      .array(z.string().min(1))
      .default([])
      .describe(
        "Reusable components, shared systems, or utilities connected to this reference",
      ),
    why_it_matters: z
      .string()
      .min(1)
      .max(1200)
      .describe(
        "Why this reference is important for understanding the project",
      ),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.path_kind === "file" && value.line_ranges.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "line_ranges must include at least one range for file paths",
        path: ["line_ranges"],
      });
    }
  });

export const repoSnippetsSchema = z
  .object({
    references: z
      .array(repoSnippetReferenceSchema)
      .min(1)
      .describe("High-signal references that anchor repository context"),
    summary: z
      .string()
      .min(1)
      .max(2000)
      .describe("High-level summary of the selected repository snippets"),
  })
  .strict();

export type RepoSnippetLineRange = z.infer<typeof repoSnippetLineRangeSchema>;
export type RepoSnippetReference = z.infer<typeof repoSnippetReferenceSchema>;
export type RepoSnippets = z.infer<typeof repoSnippetsSchema>;

const keyValueEntrySchema = z
  .object({ key: z.string().min(1), value: z.unknown() })
  .strict();

const repoProfileFacetSchema = z
  .object({ facet_type: z.string().min(1), facet_value: z.string().min(1) })
  .strict();

const repoProfileInsightPayloadSchema = z
  .object({
    additional: z.array(keyValueEntrySchema).optional(),
    architecture_pattern: z.string().max(128).optional(),
    business_domain: z.string().max(256).optional(),
    category: z.string().max(128).optional(),
    command: z.string().min(1).max(1024).optional(),
    confidence_score: z.number().min(0).max(100).optional(),
    consumers: z.string().max(2048).optional(),
    count: z.number().int().min(0).optional(),
    coverage: z.number().min(0).max(100).optional(),
    deployment_model: z.string().max(256).optional(),
    description: z.string().max(1024).optional(),
    detection_method: z.string().max(64).optional(),
    name: z.string().min(1).max(256).optional(),
    primary_language: z.string().max(64).optional(),
    provides: z.string().max(2048).optional(),
    repository_role: z.string().max(256).optional(),
    responsibilities: z.string().max(4096).optional(),
    source_file: z.string().max(512).optional(),
    status: z.enum(REPOSITORY_TOOL_STATUS_VALUES).optional(),
    tool_kind: z.enum(REPOSITORY_TOOL_KIND_VALUES).optional(),
    version: z.string().max(128).optional(),
    working_directory: z.string().min(1).max(512).optional(),
  })
  .strict();

const _repoProfileInsightSchema = z
  .object({
    facets: z.array(repoProfileFacetSchema).default([]),
    fingerprint: z.string().min(1).nullable().optional(),
    insight_id: z.string().min(1).optional(),
    insight_type: z.string().min(1),
    metadata: z.array(keyValueEntrySchema).nullable().optional(),
    payload: repoProfileInsightPayloadSchema.optional(),
    repository_id: z.string().min(1).nullable().optional(),
    severity: z.string().min(1).nullable().optional(),
  })
  .strict();

export const sourceControlRepoProfileSchema = z
  .object({
    profile: universalRepositorySchema,
    repo_snippets: repoSnippetsSchema.optional(),
    repository_id: z.string().min(1).nullable().optional(),
    repository_slug: z.string().min(1),
    run_item_id: z.string().min(1).optional(),
  })
  .strict();

export type SourceControlRepoProfile = z.infer<
  typeof sourceControlRepoProfileSchema
>;

export const sourceControlRepoProfileSchemaJson = coerceToJsonObject(
  z.toJSONSchema(sourceControlRepoProfileSchema),
);
